import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { ActionProxyResponse } from "@/hooks/useActionQuery";
import { useCallback, useState } from "react";

/**
 * Imperative mutation hook for write action proxy calls.
 *
 * Calls POST /v1/action-proxy/:actionId/execute — CU charged.
 */
export function useActionExecute(actionId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const execute = useCallback(
    async (payload: Record<string, unknown>, walletId: string): Promise<Record<string, unknown>> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch<ActionProxyResponse<Record<string, unknown>>>(
          `${V2_ENDPOINT}/v1/action-proxy/${actionId}/execute`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ payload, walletId }),
          },
        );

        if (!response.ok) {
          let errorMessage = `Action execution failed with status ${response.status}`;
          try {
            const errorData = await response.json();
            // details[0] contains the full error chain from the backend;
            // message is a static sentinel like "upstream action service returned an error".
            const detail = Array.isArray(errorData.details) ? errorData.details[0] : undefined;
            errorMessage = (typeof detail === "string" ? detail : null) || errorData.message || errorMessage;
          } catch {
            // Response wasn't JSON, use status error
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        if (data.code !== 0) {
          throw new Error(data.message || "Action execution failed");
        }
        if (!data.data.success) {
          const actionError = data.data.result?.error;
          throw new Error(actionError?.message || "Action returned unsuccessful result");
        }

        return data.data.result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error("Action execution failed");
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [actionId],
  );

  return { execute, isLoading, error };
}
